/**
 * 金额文本拆分与千分位（PRD §2.2.3：统一截断、千分位按语言格式化；§1.5.1：整数部分大、小数部分小一号）。
 * 只做字符串处理，不经过 JS number。
 */
import { truncate, type DecimalInput } from './decimal.js';

export interface NumberSymbols {
  group: string;
  decimal: string;
}

/** ko-KR、ja-JP、zh-CN、en-US 的默认数字符号都是「,」与「.」。 */
export const DEFAULT_NUMBER_SYMBOLS: NumberSymbols = { group: ',', decimal: '.' };

export interface DecimalParts {
  sign: '' | '-';
  /** 已加千分位的整数部分 */
  integer: string;
  /** 小数小数点符号；places 为 0 时为空串 */
  decimal: string;
  /** 小数部分（不含小数点）；places 为 0 时为空串 */
  fraction: string;
}

function groupDigits(digits: string, separator: string): string {
  if (!separator) return digits;
  let out = '';
  for (let i = 0; i < digits.length; i += 1) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += separator;
    out += digits[i];
  }
  return out;
}

/** 截断到 places 位后拆成符号、整数、小数三段。 */
export function formatDecimalParts(
  value: DecimalInput,
  places: number,
  symbols: NumberSymbols = DEFAULT_NUMBER_SYMBOLS,
): DecimalParts {
  const fixed = truncate(value, places).toFixed(places);
  const negative = fixed.startsWith('-');
  const unsigned = negative ? fixed.slice(1) : fixed;
  const [intPart = '0', fracPart = ''] = unsigned.split('.');
  return {
    sign: negative ? '-' : '',
    integer: groupDigits(intPart, symbols.group),
    decimal: places > 0 ? symbols.decimal : '',
    fraction: fracPart,
  };
}

/** 截断并加千分位，例如 formatDecimal('1234567.899', 2) === '1,234,567.89'。 */
export function formatDecimal(
  value: DecimalInput,
  places: number,
  symbols: NumberSymbols = DEFAULT_NUMBER_SYMBOLS,
): string {
  const p = formatDecimalParts(value, places, symbols);
  return `${p.sign}${p.integer}${p.decimal}${p.fraction}`;
}
